import { useState, useCallback, useEffect } from 'react'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Clock, Plus, X } from 'lucide-react'
import { TimeRangePicker } from './TimeRangePicker'
import {
  type TimeRange,
  type DaySchedule,
  type WeeklySchedule,
  OfficeHoursValidator,
  DEFAULT_WEEKLY_SCHEDULE,
} from '@super/shared-types'

interface WeeklyHoursScheduleProps {
  value?: WeeklySchedule
  onChange?: (schedule: WeeklySchedule) => void
  className?: string
}

type DayKey = keyof WeeklySchedule

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const

const DEFAULT_TIME_RANGE: TimeRange = { from: '09:00', to: '17:00' }
const DEFAULT_DAY_SCHEDULE: DaySchedule = {
  enabled: false,
  timeRanges: [DEFAULT_TIME_RANGE],
}

const defaultWeeklySchedule: WeeklySchedule = DEFAULT_WEEKLY_SCHEDULE

const parseTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

function useWeeklySchedule(
  initialValue: WeeklySchedule,
  onChange?: (schedule: WeeklySchedule) => void,
) {
  const [schedule, setSchedule] = useState<WeeklySchedule>(initialValue)

  // Update internal state when the initial value changes (e.g., when data is loaded from API)
  useEffect(() => {
    setSchedule(initialValue)
  }, [initialValue])

  const updateSchedule = useCallback(
    (newSchedule: WeeklySchedule) => {
      setSchedule(newSchedule)
      onChange?.(newSchedule)
    },
    [onChange],
  )

  const toggleDay = useCallback(
    (dayKey: DayKey) => {
      const currentDay = schedule[dayKey] ?? DEFAULT_DAY_SCHEDULE
      const newSchedule = {
        ...schedule,
        [dayKey]: {
          ...currentDay,
          enabled: !currentDay.enabled,
        },
      }
      updateSchedule(newSchedule)
    },
    [schedule, updateSchedule],
  )

  const updateTimeRange = useCallback(
    (
      dayKey: DayKey,
      rangeIndex: number,
      field: 'from' | 'to',
      value: string,
    ) => {
      const currentDay = schedule[dayKey] ?? DEFAULT_DAY_SCHEDULE
      const newSchedule = {
        ...schedule,
        [dayKey]: {
          ...currentDay,
          timeRanges: currentDay.timeRanges.map((range, index) =>
            index === rangeIndex ? { ...range, [field]: value } : range,
          ),
        },
      }
      updateSchedule(newSchedule)
    },
    [schedule, updateSchedule],
  )

  const addTimeRange = useCallback(
    (dayKey: DayKey) => {
      const daySchedule = schedule[dayKey] ?? DEFAULT_DAY_SCHEDULE
      if (daySchedule.timeRanges.length >= 2) return

      const newSchedule = {
        ...schedule,
        [dayKey]: {
          ...daySchedule,
          timeRanges: [...daySchedule.timeRanges, DEFAULT_TIME_RANGE],
        },
      }
      updateSchedule(newSchedule)
    },
    [schedule, updateSchedule],
  )

  const removeTimeRange = useCallback(
    (dayKey: DayKey, rangeIndex: number) => {
      const currentDay = schedule[dayKey] ?? DEFAULT_DAY_SCHEDULE
      const newSchedule = {
        ...schedule,
        [dayKey]: {
          ...currentDay,
          timeRanges: currentDay.timeRanges.filter(
            (_, index) => index !== rangeIndex,
          ),
        },
      }
      updateSchedule(newSchedule)
    },
    [schedule, updateSchedule],
  )

  return {
    schedule,
    toggleDay,
    updateTimeRange,
    addTimeRange,
    removeTimeRange,
  }
}

interface DayScheduleRowProps {
  dayKey: DayKey
  label: string
  daySchedule: DaySchedule
  onToggleDay: (dayKey: DayKey) => void
  onUpdateTimeRange: (
    dayKey: DayKey,
    rangeIndex: number,
    field: 'from' | 'to',
    value: string,
  ) => void
  onAddTimeRange: (dayKey: DayKey) => void
  onRemoveTimeRange: (dayKey: DayKey, rangeIndex: number) => void
}

function DayScheduleRow({
  dayKey,
  label,
  daySchedule,
  onToggleDay,
  onUpdateTimeRange,
  onAddTimeRange,
  onRemoveTimeRange,
}: DayScheduleRowProps) {
  const hasOverlap =
    daySchedule.enabled &&
    OfficeHoursValidator.hasTimeRangeOverlap(daySchedule.timeRanges)

  const isRangeInOverlap = (rangeIndex: number): boolean => {
    if (!hasOverlap) return false

    const currentRange = daySchedule.timeRanges[rangeIndex]
    return daySchedule.timeRanges.some((otherRange, otherIndex) => {
      if (otherIndex === rangeIndex) return false

      const start1 = parseTime(currentRange.from)
      const end1 = parseTime(currentRange.to)
      const start2 = parseTime(otherRange.from)
      const end2 = parseTime(otherRange.to)

      return start1 < end2 && start2 < end1
    })
  }

  return (
    <div className="pr-10">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Switch
            checked={daySchedule.enabled}
            onCheckedChange={() => onToggleDay(dayKey)}
          />
          <Label className="text-sm font-medium">{label}</Label>
        </div>

        <div className="flex items-center relative">
          <div className="flex items-center space-x-2 p-1">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <TimeRangePicker
              value={daySchedule.timeRanges[0].from}
              onChange={value => onUpdateTimeRange(dayKey, 0, 'from', value)}
              maxTime={daySchedule.timeRanges[0].to}
              disabled={!daySchedule.enabled}
              startAdornment="From"
              error={isRangeInOverlap(0)}
            />
            <TimeRangePicker
              value={daySchedule.timeRanges[0].to}
              onChange={value => onUpdateTimeRange(dayKey, 0, 'to', value)}
              minTime={daySchedule.timeRanges[0].from}
              disabled={!daySchedule.enabled}
              startAdornment="To"
              error={isRangeInOverlap(0)}
            />
          </div>
          {/* Always reserve space for the button to maintain consistent width */}
          <div className="w-8 h-8 flex items-center justify-center absolute -right-10">
            {daySchedule.timeRanges.length < 2 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAddTimeRange(dayKey)}
                className="h-8 w-8 p-0"
                disabled={!daySchedule.enabled}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {daySchedule.timeRanges.length > 1 && (
        <div>
          {daySchedule.timeRanges.slice(1).map((timeRange, index) => (
            <div key={index + 1} className="flex items-center justify-between">
              {/* Dummy element to match the left side layout */}
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5" />
                <div className="w-16" />
              </div>

              <div className="flex items-center relative">
                <div className="flex items-center space-x-2 p-1">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <TimeRangePicker
                    value={timeRange.from}
                    onChange={value =>
                      onUpdateTimeRange(dayKey, index + 1, 'from', value)
                    }
                    maxTime={timeRange.to}
                    disabled={!daySchedule.enabled}
                    startAdornment="From"
                    error={isRangeInOverlap(index + 1)}
                  />
                  <TimeRangePicker
                    value={timeRange.to}
                    onChange={value =>
                      onUpdateTimeRange(dayKey, index + 1, 'to', value)
                    }
                    minTime={timeRange.from}
                    disabled={!daySchedule.enabled}
                    startAdornment="To"
                    error={isRangeInOverlap(index + 1)}
                  />
                </div>
                {/* Always reserve space for the button to maintain consistent width */}
                <div className="w-8 h-8 flex items-center justify-center absolute -right-10">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveTimeRange(dayKey, index + 1)}
                    className="h-8 w-8 p-0"
                    disabled={!daySchedule.enabled}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {hasOverlap && (
            <div className="space-x-2">
              <div className="text-xs text-red-900 text-right block pr-3">
                Please adjust the times to avoid overlap.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function WeeklyHoursSchedule({
  value = defaultWeeklySchedule,
  onChange,
  className,
}: WeeklyHoursScheduleProps) {
  const {
    schedule,
    toggleDay,
    updateTimeRange,
    addTimeRange,
    removeTimeRange,
  } = useWeeklySchedule(value, onChange)

  return (
    <div className={className}>
      <div className="space-y-4">
        {DAYS.map(({ key, label }) => (
          <DayScheduleRow
            key={key}
            dayKey={key}
            label={label}
            daySchedule={schedule[key] ?? DEFAULT_DAY_SCHEDULE}
            onToggleDay={toggleDay}
            onUpdateTimeRange={updateTimeRange}
            onAddTimeRange={addTimeRange}
            onRemoveTimeRange={removeTimeRange}
          />
        ))}
      </div>
    </div>
  )
}
